import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import Loading from "../../components/Loading";
import { formatManagerDate } from "../../helper/managerDate";

const severityClass = {
  hot: "border-red-400 bg-red-50 dark:bg-red-900/20",
  warn: "border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20",
  good: "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20",
  info: "border-blue-300 bg-blue-50 dark:bg-blue-900/20",
};

const ManagerInbox = () => {
  const { userId, manager, loading, refresh } = useVirtualWorld();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  /** Sofort-Ergebnis an der Karte, ohne nach oben zu scrollen */
  const [localOutcomes, setLocalOutcomes] = useState({});
  const cardRefs = useRef({});

  const load = useCallback(() => {
    if (!userId) return;
    vw.inbox(userId)
      .then((res) => setData(res.data))
      .catch(() => setData({ items: [], unread: 0 }));
  }, [userId]);

  useEffect(load, [load]);

  if (loading) return <Loading />;
  if (!manager?.club) return <p>Bitte zuerst einen Club gründen.</p>;
  if (!data) return <Loading />;

  const respond = async (eventId, choiceKey) => {
    setBusy(eventId);
    try {
      const res = await vw.inboxRespond(userId, eventId, choiceKey);
      if (res.data?.redirect) {
        await refresh();
        navigate(res.data.redirect);
        return;
      }
      const outcome = res.data?.event?.outcome || res.data?.outcome || "Gespeichert.";
      setLocalOutcomes((prev) => ({ ...prev, [eventId]: outcome }));
      if (res.data?.keepOpen) {
        toast.warning(outcome, { autoClose: 8000, toastId: `inbox-${eventId}` });
      } else {
        toast.success(outcome, { autoClose: 7000, toastId: `inbox-${eventId}` });
      }
      // Karte im Viewport halten / Ergebnis sichtbar machen — nicht zur Seiten-Spitze
      requestAnimationFrame(() => {
        cardRefs.current[eventId]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      await refresh();
      load();
    } catch (err) {
      const note = err?.data?.message || "Antwort fehlgeschlagen.";
      setLocalOutcomes((prev) => ({ ...prev, [eventId]: note }));
      toast.warning(note, { autoClose: 8000, toastId: `inbox-err-${eventId}` });
    } finally {
      setBusy("");
    }
  };

  const readAll = async () => {
    setBusy("all");
    try {
      const res = await vw.inboxReadAll(userId);
      setData(res.data);
      await refresh();
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-900 text-white rounded-3xl p-6 border border-yellow-400">
        <div className="text-sm uppercase tracking-wide text-yellow-200 mb-1">Boardroom</div>
        <h2 className="text-4xl font-black mb-2">📬 Inbox</h2>
        <p className="opacity-80 text-sm mb-4">
          Offene Entscheidungen zählen im Badge. Veraltetes (Bus nach Spiel, erledigte Transfers,
          alte Infos) wird automatisch abgelegt.
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="rounded-xl bg-white/10 px-4 py-2">
            <span className="text-xs opacity-70">Zu entscheiden</span>
            <div className="text-2xl font-black">
              {Number(data.actionable ?? data.unread ?? 0)}
            </div>
          </div>
          {Number(data.unreadAll || 0) > Number(data.actionable ?? data.unread ?? 0) ? (
            <div className="rounded-xl bg-white/10 px-4 py-2">
              <span className="text-xs opacity-70">Nur Info ungelesen</span>
              <div className="text-lg font-bold">
                {Number(data.unreadAll || 0) - Number(data.actionable ?? data.unread ?? 0)}
              </div>
            </div>
          ) : null}
          <button
            type="button"
            disabled={
              busy === "all" ||
              !(Number(data.unreadAll || 0) > 0 || Number(data.unread || 0) > 0)
            }
            onClick={readAll}
            className="px-4 py-2 rounded-xl bg-white text-black font-semibold disabled:opacity-40"
          >
            Alle als gelesen
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {(data.items || []).map((ev) => {
          const outcomeText = localOutcomes[ev.id] || ev.outcome;
          const justActed = Boolean(localOutcomes[ev.id]);
          return (
          <div
            key={ev.id}
            ref={(el) => {
              if (el) cardRefs.current[ev.id] = el;
              else delete cardRefs.current[ev.id];
            }}
            className={`rounded-2xl border p-4 ${severityClass[ev.severity] || severityClass.info} ${
              ev.status === "unread" ? "ring-2 ring-yellow-400" : ""
            } ${justActed ? "ring-2 ring-emerald-500" : ""} ${
              !justActed && (ev.status === "acted" || ev.status === "expired") ? "opacity-70" : ""
            }`}
          >
            <div className="flex justify-between gap-3 mb-2">
              <div>
                <div className="text-xs uppercase opacity-60">
                  {ev.category} ·{" "}
                  {ev.status === "unread"
                    ? ev.actionable
                      ? "ENTSCHEIDUNG"
                      : "NEU"
                    : ev.status === "expired"
                      ? "erledigt"
                      : ev.status}
                  {ev.matchday ? ` · ST ${ev.matchday}` : ""}
                  {ev.createdAt ? ` · ${formatManagerDate(ev.createdAt)}` : ""}
                </div>
                <div className="font-black text-xl">
                  {ev.emoji} {ev.title}
                </div>
                {ev.expiresAt ? (
                  <div className="text-xs opacity-70 mt-0.5">Frist: {formatManagerDate(ev.expiresAt)}</div>
                ) : null}
              </div>
              {ev.status === "unread" ? (
                <button
                  type="button"
                  className="text-xs underline opacity-70"
                  onClick={async () => {
                    await vw.inboxRead(userId, ev.id);
                    load();
                    refresh();
                  }}
                >
                  Gelesen
                </button>
              ) : null}
            </div>
            <p className="text-sm mb-3">{ev.body}</p>
            {outcomeText ? (
              <p
                className={`text-sm font-semibold mb-2 rounded-xl px-3 py-2 ${
                  justActed
                    ? "bg-emerald-600 text-white"
                    : "bg-black/5 dark:bg-white/10"
                }`}
              >
                Ergebnis: {outcomeText}
              </p>
            ) : null}
            {ev.status !== "acted" &&
            ev.status !== "expired" &&
            !localOutcomes[ev.id] &&
            ev.choices?.length ? (
              <div className="flex flex-wrap gap-2">
                {ev.choices.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => respond(ev.id, c.key)}
                    className="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold border border-yellow-400 disabled:opacity-50"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          );
        })}
        {!data.items?.length ? (
          <p className="opacity-70">Noch keine Meldungen. Nach Clubgründung und Spieltagen füllt sich die Inbox.</p>
        ) : null}
      </div>
    </div>
  );
};

export default ManagerInbox;
